import React, { useState } from "react";
import { Link } from "react-router-dom";
import styles from "../styles/Footer.module.css";
import logo from "../assets/8insights-logo.svg";

/* Footer link columns (data-driven so we can render an accordion on mobile).
   `to` = internal route (React Router Link); `href` = external/static link. */
const LINK_COLUMNS = [
  {
    title: "Services",
    links: [
      { label: "Strategic Marketing", to: "/what-we-do" },
      { label: "SEO & AIO", to: "/what-we-do" },
      { label: "GEO", to: "/what-we-do/geo" },
      { label: "ORM & Social Media", to: "/what-we-do" },
      { label: "Web & App Development", to: "/what-we-do" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About Us", to: "/about" },
      { label: "What We Do", to: "/what-we-do" },
      { label: "Join Us", to: "/join-us" },
      { label: "Contact", to: "/contact" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "Learn", to: "/learn" },
      { label: "Insights", href: "#" },
      { label: "Case Studies", href: "#" },
      { label: "FAQs", href: "#" },
      { label: "Sitemap", href: "/sitemap.xml" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Privacy Policy", to: "/privacy-policy" },
      { label: "Terms of Use", to: "/terms-of-use" },
      { label: "Cookie Policy", to: "/cookie-policy" },
      { label: "Disclaimer", to: "/disclaimer" },
    ],
  },
];

const Footer = () => {
  // Which column is expanded (mobile accordion only). Desktop shows all via CSS.
  // Default: first category (Services) open.
  const [openCol, setOpenCol] = useState(0);
  const toggleCol = (i) => setOpenCol((prev) => (prev === i ? null : i));

  return (
    <footer className={styles.footer} id="contact-us">
      {/* ===================== PART 1 ===================== */}
      <div className={styles.top}>
        <div className={styles.inner}>
          {/* Col 1: Logo + Address */}
          <div className={styles.col}>
            <Link to="/" className={styles.brand}>
              <img src={logo} alt="8 Insights Logo" className={styles.logo} />
            </Link>

            <p className={styles.addressLabel}>Head Office</p>

            <address className={styles.addressWrap}>
              <i className={`bi bi-geo-alt-fill ${styles.addressIcon}`} aria-hidden="true" />
              <p className={styles.address}>
                Level 20, <br />
                One International Finance Centre,<br />
                1 Harbour View Street,<br />
                Central, Hong Kong
              </p>
            </address>
          </div>

          {/* Col 2: Why we matter */}
          {/* <div className={styles.col}>
            <h3 className={styles.title}>Why We Matter</h3>
            <p className={styles.text}>
              We are dedicated to delivering clarity, creativity, and commercially
              impactful solutions for every business we partner with.
            </p>
          </div> */}

          {/* Col 2: Newsletter Subscription */}
          <div className={styles.col}>
            <h3 className={styles.title}>Stay Updated</h3>

            <p className={styles.text}>
              Subscribe to receive insights on strategy, digital growth, and performance-driven marketing.
            </p>

            <form className={styles.subscribeForm} onSubmit={(e) => e.preventDefault()}>
              <label htmlFor="footer-subscribe-email" className={styles.srOnly}>
                Email address
              </label>
              <input
                id="footer-subscribe-email"
                name="email"
                type="email"
                autoComplete="email"
                placeholder="Enter your email address"
                className={styles.subscribeInput}
                required
              />
              <button type="submit" className={styles.subscribeBtn}>
                Subscribe
              </button>
            </form>
          </div>



          {/* Col 3: Follow Us + LinkedIn */}
          <div className={styles.col}>
            <h3 className={styles.title}>Follow Us</h3>

            <a
              href="https://www.linkedin.com/company/8-insights/"
              target="_blank"
              rel="noreferrer"
              className={styles.socialLink}
              aria-label="8 Insights on LinkedIn"
            >
              <span> <i className="bi bi-linkedin" /> </span>
            </a>

            {/* Find us on — Evolve Journey app */}
            <p className={styles.appLabel}>Find us on</p>
            <div className={styles.appBadges}>
              {/* TODO: replace href="#" with the Evolve Journey App Store URL */}
              <a
                href="#"
                target="_blank"
                rel="noreferrer"
                aria-label="Download Evolve Journey on the App Store"
              >
                <img
                  className={styles.badgeApple}
                  src="/badges/app-store-badge.svg"
                  alt="Download on the App Store"
                />
              </a>
              {/* TODO: replace href="#" with the Evolve Journey Google Play URL */}
              <a
                href="#"
                target="_blank"
                rel="noreferrer"
                aria-label="Get Evolve Journey on Google Play"
              >
                <img
                  className={styles.badgeGoogle}
                  src="/badges/google-play-badge.png"
                  alt="Get it on Google Play"
                />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* ===================== PART 2 ===================== */}
      <div className={styles.links}>
        <div className={styles.inner}>
          <div className={styles.linksGrid}>
            {LINK_COLUMNS.map((col, i) => {
              const isOpen = openCol === i;
              return (
                <div
                  className={`${styles.linksCol} ${isOpen ? styles.linksColOpen : ""}`}
                  key={col.title}
                >
                  {/* Heading: a real <button> so it's tappable/accessible on mobile.
                      On desktop CSS makes it a plain heading and always shows the list. */}
                  <button
                    type="button"
                    className={styles.linksTitle}
                    onClick={() => toggleCol(i)}
                    aria-expanded={isOpen}
                    aria-controls={`footer-col-${i}`}
                  >
                    {col.title}
                    <i className={`bi bi-chevron-down ${styles.linksChevron}`} aria-hidden="true" />
                  </button>

                  <ul id={`footer-col-${i}`} className={styles.list}>
                    {col.links.map((link) => (
                      <li key={link.label}>
                        {link.to ? (
                          <Link to={link.to}>{link.label}</Link>
                        ) : (
                          <a href={link.href}>{link.label}</a>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          {/* Bottom strip */}
          <div className={styles.bottom}>
            <p>© {new Date().getFullYear()} || 8-Insights. All rights reserved.</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
